import { useEffect, useState } from 'react';
import Router from 'next/router';
import { format } from 'date-fns';
import api from 'api';
import { filterValuesSeparator } from 'shared-helpers/consts';
import { getMessageWithPlaceholders, localeNames } from 'shared-locales';
import { useAchievementTypes, useDispatch, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  Avatar,
  ContentLabel,
  EmptyIndicator,
  TitleWithIcon,
  Button,
  LoadingIndicator,
  RichTextValue,
  Tooltip
} from 'components/common';
import { Box, BoxBackLink, BoxRow } from 'components/common/box';
import { DateRangeSelector } from 'components/common/charts';
import { DropdownSelectable } from 'components/common/dropdown';
import {
  Filters,
  FiltersActive,
  useAppliedFilterParams
} from 'components/common/filter';
import { FormGroup } from 'components/common/form';
import { CheckboxInput, SelectInput } from 'components/common/form/common';
import { Modal, ModalContent, ModalPortal } from 'components/common/modal';

import type { Locales } from 'shared-types';
import type {
  GetAchievementsRes,
  GetUserCountByAdminRes
} from 'shared-types/responses';

type Range = 'default' | 'months' | 'quarters' | 'years';

const AchievementConversationAnalysesNew = () => {
  const { dispatch } = useDispatch();

  const { language, messages, theme, user } = useReduxState();

  const [processing, setProcessing] = useState(false);
  const [itemIds, setItemIds] = useState<Record<string, number>>({});
  const [chosenLanguage, setChosenLanguage] = useState<Locales>(
    language || 'en-GB'
  );
  const [numberOfUsers, setNumberOfUsers] = useState<null | number>(0);
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const [range, setRange] = useState<Range>('default');
  const [selectedAchievement, setSelectedAchievement] = useState<
    GetAchievementsRes['achievements'][0] | null
  >(null);
  const [showInProgressModal, setShowInProgressModal] = useState(false);

  const { data, loading } = useApiData<GetAchievementsRes>({
    onlyIf: Boolean(user?.id),
    url:
      '/admin/achievements?achievementType=' +
      ['conversation', 'group-conversation'].join(filterValuesSeparator)
  });

  const filterId = 'conversation-analyses-new';
  const appliedFilters = useAppliedFilterParams(filterId);

  const achievementTypes = useAchievementTypes();

  useEffect(() => {
    if (!selectedAchievement?.id) return;

    const getUserCount = async () => {
      try {
        const params = new URLSearchParams(appliedFilters);

        const response = await api.get<GetUserCountByAdminRes>(
          `/admin/users-admin-count?achievementId=${selectedAchievement.id}` +
            (params.size ? `&${params.toString()}` : '')
        );
        if (response.data.success) {
          setNumberOfUsers(response.data.numberOfUsers);
        }
      } catch {
        setNumberOfUsers(null);
      }
    };

    getUserCount();
  }, [appliedFilters, selectedAchievement]);

  const handleAchievementSelected = (achievementId: number) => {
    const achievement = data?.achievements?.find(
      (achievement) => achievement.id === achievementId
    );
    setSelectedAchievement(achievement || null);
    setItemIds(
      achievement?.template?.reduce((acc, templateItem) => {
        acc[templateItem.id] = templateItem.id;
        return acc;
      }, {} as Record<string, number>) || {}
    );
  };

  const handleDateChange = ({
    fromDate,
    toDate
  }: Record<'fromDate' | 'toDate', string | null>) => {
    const comparisonFormat = 'dd.MM.yyyy';
    const now = format(Date.now(), comparisonFormat);

    // this component always returns a valid date, but any unset date is just end
    // of today. We want to make sure we only pass relevant dates to the API, so
    // we check if the date is the end of today and if so, we unset it.
    const formattedFromDate = format(Date.parse(fromDate), comparisonFormat);
    if (formattedFromDate === now) {
      fromDate = undefined;
    }

    const formattedToDate = format(Date.parse(toDate), comparisonFormat);
    if (formattedToDate === now) {
      toDate = undefined;
    }

    setFromDate(fromDate);
    setToDate(toDate);
  };

  const handleQuestionClicked = (questionId: number, checked: boolean) => {
    const newItemIds = { ...itemIds };
    if (checked) newItemIds[questionId] = questionId;
    else delete newItemIds[questionId];

    setItemIds(newItemIds);
  };

  const handleSubmit = async () => {
    if (!selectedAchievement) return;

    setProcessing(true);
    setShowInProgressModal(true);

    try {
      const payload = {
        achievementItemIds: Object.values(itemIds),
        language: chosenLanguage,
        ...appliedFilters,
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
        ...(range && range !== 'default' ? { range } : {})
      };

      const url = `/admin/achievements/${selectedAchievement.id}/discussion-analysis`;
      const response = await api.post(url, payload);

      dispatch.addNotice({
        text: messages.achievementConversationAnalysesSuccess,
        type: 'success'
      });

      Router.push(
        `/achievements/conversation-analyses/${response?.data?.analysisResultId}`
      );
    } catch (err) {
      if (err.response.data.error === 'not_enough_valid_data_to_analyse') {
        dispatch.addNotice({
          text: messages.achievementConversationAnalysesNotEnoughValidData,
          type: 'warning'
        });
      } else {
        await dispatch.handleErrorDispatch(err);
      }
    } finally {
      setProcessing(false);
      setShowInProgressModal(false);
    }
  };

  const achievementOptions =
    data?.achievements?.map(({ id, title, type }) => ({
      helpText: achievementTypes[type]?.title,
      label: title,
      value: id
    })) || [];

  const questions = selectedAchievement?.template || [];

  const numberOfQuestions = Object.values(itemIds).length;

  const submitDisabled =
    !selectedAchievement ||
    !numberOfUsers ||
    numberOfUsers < 5 ||
    !numberOfQuestions ||
    processing;

  return (
    <div className="conversations-new">
      {loading ? (
        <LoadingIndicator />
      ) : (
        <div className="inner">
          <Box
            title={
              <BoxBackLink
                as="/achievements/conversation-analyses"
                href="/achievements/conversation-analyses"
                text={messages.achievementConversationAnalyses}
              />
            }
          >
            <div className="title">
              <TitleWithIcon
                icon="chart-line"
                style={{ alignItems: 'center', display: 'flex' }}
                title={messages.achievementConversationAnalysesNew}
              />
            </div>

            <FormGroup
              label={messages.eventCalendarAchievementSelect}
              style={{ marginTop: 25 }}
            >
              <SelectInput
                isDisabled={processing}
                id="achievement-id"
                isSearchable={true}
                noOptionsMessage={() => messages.achievementsEmpty}
                onChange={(achievement: (typeof achievementOptions)[0]) =>
                  handleAchievementSelected(achievement.value)
                }
                options={achievementOptions}
                placeholder={messages.eventCalendarAchievementSelect}
                value={achievementOptions.find(
                  (option) => option.value === selectedAchievement?.id
                )}
              />
            </FormGroup>
          </Box>

          <Box
            title={messages.commonQuestions}
            titleDescription={
              messages.achievementConversationAnalysesQuestionsInfo
            }
          >
            {questions.length ? (
              <div className="questions">
                {questions.map((templateItem) => (
                  <div className="question" key={templateItem.id}>
                    <CheckboxInput
                      checked={Boolean(itemIds[templateItem.id])}
                      disabled={processing}
                      iconStyle={{ marginTop: 4 }}
                      id={`ai-analysis-conversations-question-select-${templateItem.id}`}
                      label={
                        <div className="question-label">
                          <div className="question-number">
                            <Avatar fontSize="1.4rem">
                              {templateItem.order}
                            </Avatar>
                          </div>

                          <div className="question-text">
                            <RichTextValue html={templateItem.text} />
                          </div>
                        </div>
                      }
                      onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                        handleQuestionClicked(
                          templateItem.id,
                          ev.target.checked
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyIndicator
                icon="comments"
                size="small"
                title={messages.commonNoQuestions}
              />
            )}
          </Box>

          <Box padding={0} title={messages.commonSettings}>
            <BoxRow padding="20px 15px">
              <ContentLabel
                horizontal={true}
                label={messages.commonFiltersUsers}
                labelStyle={{ minWidth: 150 }}
              >
                <div className="filters-users">
                  <Filters
                    allowMultiple={{
                      groups: true,
                      supervisors: true,
                      userAttributes: true
                    }}
                    filterId={filterId}
                    filters={{
                      accountAccess: true,
                      accountIdentifier: true,
                      accountLogin: true,
                      accountType: true,
                      groups: true,
                      supervisors: true,
                      userAttributes: true
                    }}
                    syncWithUrl={false}
                    triggerProps={{
                      disabled: processing,
                      icon: 'user',
                      text: messages.commonFiltersUsers
                    }}
                  />
                </div>

                <div className="filters-active">
                  <FiltersActive filterId={filterId} />
                </div>
              </ContentLabel>
            </BoxRow>

            <BoxRow padding="20px 15px">
              <ContentLabel
                horizontal={true}
                label={messages.achievementConversationAnalysesTrend}
                labelStyle={{ minWidth: 150 }}
              >
                <DropdownSelectable
                  category="lighter"
                  disabled={processing}
                  defaultValue="default"
                  options={[
                    {
                      text: messages.commonDefault,
                      value: 'default',
                      subText:
                        messages.achievementConversationAnalysesTrendHelpText
                    },
                    { text: messages.commonTimeMonths, value: 'months' },
                    {
                      text: messages.commonTimeQuarters,
                      value: 'quarters'
                    },
                    { text: messages.commonTimeYears, value: 'years' }
                  ]}
                  onChange={(value) => setRange(value as Range)}
                  showSelectedText={true}
                  value={range}
                />
              </ContentLabel>
            </BoxRow>

            <BoxRow padding="20px 15px">
              <ContentLabel
                horizontal={true}
                label={messages.commonDateRange}
                labelStyle={{ minWidth: 150 }}
              >
                <DateRangeSelector
                  align="right"
                  includeAllTimeOption={true}
                  size="header"
                  updateData={handleDateChange}
                />
              </ContentLabel>
            </BoxRow>

            <BoxRow padding="20px 15px">
              <ContentLabel
                horizontal={true}
                label={messages.commonLanguage}
                labelStyle={{ minWidth: 150 }}
              >
                <DropdownSelectable
                  category="lighter"
                  disabled={processing}
                  defaultValue={language || 'en-GB'}
                  options={Object.keys(localeNames).map((locale: Locales) => ({
                    text: localeNames[locale],
                    value: locale
                  }))}
                  onChange={(value) => setChosenLanguage(value)}
                  showSelectedText={true}
                />
              </ContentLabel>
            </BoxRow>
          </Box>

          <Box>
            <div className="summary">
              <div className="summary-items">
                <Tooltip
                  content={
                    !numberOfQuestions
                      ? messages.achievementConversationAnalysesNotEnoughQuestions
                      : undefined
                  }
                >
                  <div className="summary-item">
                    <Avatar
                      icon="comment"
                      padding="6px 0"
                      {...(!numberOfQuestions
                        ? {
                            backgroundColor: theme.dangerColor,
                            color: theme.dangerTextColor
                          }
                        : {})}
                    />

                    {getMessageWithPlaceholders({
                      message: messages.commonNumberOfQuestions,
                      placeholders: {
                        numberOfQuestions: `${numberOfQuestions} / ${questions.length}`
                      }
                    })}
                  </div>
                </Tooltip>

                {typeof numberOfUsers === 'number' ? (
                  <Tooltip
                    content={
                      numberOfUsers < 5
                        ? messages.achievementConversationAnalysesNotEnoughUsers
                        : undefined
                    }
                  >
                    <div className="summary-item">
                      <Avatar
                        icon="user"
                        padding="6px 0"
                        {...(numberOfUsers < 5
                          ? {
                              backgroundColor: theme.dangerColor,
                              color: theme.dangerTextColor
                            }
                          : {})}
                      />
                      {numberOfUsers}{' '}
                      {(numberOfUsers === 1
                        ? messages.commonUser
                        : messages.commonUsers
                      ).toLowerCase()}
                    </div>
                  </Tooltip>
                ) : null}
              </div>

              <div className="summary-start">
                <Button
                  category="success"
                  disabled={submitDisabled}
                  icon="play"
                  loading={processing}
                  onClick={handleSubmit}
                  text={messages.achievementConversationAnalysesStart}
                />
              </div>
            </div>
          </Box>

          <ModalPortal>
            <Modal
              closeButton={true}
              onHide={() => setShowInProgressModal(false)}
              title={messages.achievementConversationAnalyses}
              visible={showInProgressModal}
            >
              <ModalContent
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 20
                }}
              >
                <LoadingIndicator padding={20} />
                {messages.achievementConversationAnalysesInProgress}
              </ModalContent>
            </Modal>
          </ModalPortal>
        </div>
      )}

      <style jsx>{`
        .inner {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }
        .questions {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        .question :global(label) {
          align-items: flex-start;
          border: 2px dashed ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius};
          display: flex;
          gap: 15px;
          padding: 15px;
          transition: border 0.3s;
        }
        .question :global(label:hover) {
          border: 2px dashed ${theme.lightGreyDarkActive};
        }
        .question :global(input:checked + label) {
          border: 2px solid ${theme.lightGreyDarkActive};
        }
        .question-label {
          display: flex;
          gap: 10px;
        }
        .question-number {
          flex-grow: 0;
          flex-shrink: 0;
        }
        .question-text {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          flex-shrink: 1;
          justify-content: center;
          line-height: 1.4;
          min-height: 28px;
        }
        .question-select {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.buttonBorderRadius};
          flex-grow: 0;
          flex-shrink: 0;
          padding: 3px 6px;
        }
        .filters {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: space-between;
        }
        .filters-left {
          display: flex;
          gap: 10px;
        }
        .summary {
          align-items: center;
          display: flex;
          gap: 30px;
        }
        .summary-items {
          display: flex;
          flex-grow: 1;
          flex-shrink: 1;
          gap: 50px;
        }
        .summary-item {
          align-items: center;
          color: ${theme.textColorLighter};
          display: flex;
          font-size: 1.6rem;
          font-weight: 600;
          gap: 10px;
        }
        .summary-start {
          flex-grow: 0;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
};

export default AchievementConversationAnalysesNew;
